import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import React, {useEffect, useState} from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
// routes
import { paths } from 'src/routes/paths';
import { useSearchParams, useRouter } from 'src/routes/hooks';
// config
import { PATH_AFTER_LOGIN } from 'src/config-global';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// auth
import { useAuthContext } from 'src/auth/hooks';
// components
import Iconify from 'src/components/iconify';
import FormProvider, {RHFSelect, RHFTextField} from 'src/components/hook-form';
import MenuItem from "@mui/material/MenuItem";
import {getGroupSchools} from "../../../api/city";
import {getSchoolYear} from "../../../utils/problems";

// ----------------------------------------------------------------------

export default function JwtLoginSchoolView() {
  const { login } = useAuthContext();

  const router = useRouter();

  const [errorMsg, setErrorMsg] = useState('');

  const searchParams = useSearchParams();
  const [groupSchools, setGroupSchools] = useState<{id: string, name: string}[]>([]);

  const returnTo = searchParams.get('returnTo');

  const password = useBoolean();

  const LoginSchema = Yup.object().shape({
    email: Yup.string().required('Имейлът е задължителен').email('Трябва да е валиден имейл адрес'),
    password: Yup.string().required('Паролата е задължителна'),
    school: Yup.string().required('Училището е задължително'),
    schoolYear: Yup.string().required('Учебната година е задължителна'),
  });

  const defaultValues = {
    email: '',
    password: '',
    school: '6to',
    schoolYear: getSchoolYear(new Date()).toString(),
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    if (!groupSchools.length) {
      getGroupSchools().then((data) => {
        setGroupSchools(data.schools);
      });
    }
  }, [groupSchools.length]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      await login?.(data.email, data.password, data.school, Number(data.schoolYear));

      router.push(returnTo || PATH_AFTER_LOGIN);
    } catch (error) {
      console.error(error);
      setErrorMsg(typeof error === 'string' ? error : error.message);
    }
  });

  const renderHead = (
    <Stack spacing={2} sx={{ mb: 5 }}>
      <Typography variant="h4">Вход с училищен акаунт</Typography>
    </Stack>
  );

  const renderForm = (
    <Stack spacing={2.5}>
      {!!errorMsg && <Alert severity="error">{errorMsg}</Alert>}

      <RHFTextField name="email" label="Имейл" />

      <RHFTextField
        name="password"
        label="Парола"
        type={password.value ? 'text' : 'password'}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={password.onToggle} edge="end">
                <Iconify icon={password.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <RHFSelect
        fullWidth
        name="school"
        label="Училище"
        InputLabelProps={{ shrink: true }}
        PaperPropsSx={{ textTransform: 'capitalize' }}
      >
        {groupSchools.map((option) => (
          <MenuItem key={option.id} value={option.id}>
            {option.name}
          </MenuItem>
        ))}
        {groupSchools.length === 0 && <MenuItem value="_">Зарежда се...</MenuItem>}
      </RHFSelect>

      <RHFSelect
        fullWidth
        name="schoolYear"
        label="Уч. година"
        InputLabelProps={{ shrink: true }}
        PaperPropsSx={{ textTransform: 'capitalize' }}
      >
        {
          Array.from({ length: getSchoolYear(new Date()) - 2023 + 1 }).map((_, index) => (
            <MenuItem key={index} value={(2023 + index).toString()}>{2023 + index}/{2024 + index}</MenuItem>
          ))
        }
      </RHFSelect>


      {/* <Link variant="body2" color="inherit" underline="always" sx={{ alignSelf: 'flex-end' }}> */}
      {/*  Forgot password? */}
      {/* </Link> */}

      <LoadingButton
        fullWidth
        color="inherit"
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
      >
        Вход
      </LoadingButton>
    </Stack>
  );

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      {renderHead}

      <LoadingButton
        fullWidth
        size="large"
        onClick={() => {router.push(paths.auth.jwt.login)}}
        color="info"
        variant="contained"
        loading={isSubmitting}
      >
        Влез с личен акаунт
      </LoadingButton>

      <center style={{marginTop: 30, marginBottom: 30}}>или</center>

      {renderForm}
    </FormProvider>
  );
}
